import React, { useState, useEffect, useLayoutEffect } from 'react';
import {
    TableRow,
    TableCell,
    Collapse,
    Box,
} from '@mui/material';

export interface ExpandableRowDetailProps {
    /** Ist die Zeile expandiert? */
    isExpanded: boolean;
    /** Anzahl der Spalten in der Tabelle (für colSpan) */
    colSpan: number;
    /** Callback zum Schließen (optional, für programmatisches Schließen) */
    onClose?: () => void;
    /** Inhalt des expandierten Bereichs */
    children: React.ReactNode;
}

// Animationsdauer in ms
const ANIMATION_DURATION = 200;

/**
 * ExpandableRowDetail - Generischer Container für expandierbare Tabellenzeilen
 * 
 * Design: "Subtle Inline" - nahtlose Integration in die Tabelle
 * - Gleicher Hintergrund wie Tabellenzellen
 * - Leichte Einrückung links
 * - Kein separater Close-Button (Schließen über dieselbe Stelle wie Öffnen)
 * - Sanfte Ein-/Ausgangsanimation
 */
const ExpandableRowDetail: React.FC<ExpandableRowDetailProps> = ({
    isExpanded,
    colSpan,
    children,
}) => {
    // Separater State: shouldRender steuert ob im DOM, animateIn steuert die Animation
    const [shouldRender, setShouldRender] = useState(false);
    const [animateIn, setAnimateIn] = useState(false);

    useEffect(() => {
        if (isExpanded) {
            // Erst in DOM einfügen
            setShouldRender(true);
        } else {
            // Animation starten (Collapse)
            setAnimateIn(false);
            // Nach Animation aus DOM entfernen
            const timer = setTimeout(() => {
                setShouldRender(false);
            }, ANIMATION_DURATION);
            return () => clearTimeout(timer);
        }
    }, [isExpanded]);

    // Eingangsanimation: Nach dem Mount die Animation starten
    useLayoutEffect(() => {
        if (shouldRender && isExpanded) {
            // Kleiner Delay damit Browser die Komponente erst rendert
            const frame = requestAnimationFrame(() => {
                setAnimateIn(true);
            });
            return () => cancelAnimationFrame(frame);
        }
    }, [shouldRender, isExpanded]);

    // Nichts rendern wenn nicht im DOM
    if (!shouldRender) {
        return null;
    }

    return (
        <TableRow
            sx={{
                // Keine Hover-Effekte für die Detail-Zeile
                '&:hover': {
                    transform: 'none !important',
                    boxShadow: 'none !important',
                },
                // Verstecke die Zeile komplett wenn Animation beendet
                '& > td': {
                    borderBottom: 'none',
                    padding: 0,
                    // Wichtig: Keine minimale Höhe
                    height: 0,
                    lineHeight: 0,
                },
            }}
        >
            <TableCell
                colSpan={colSpan}
                sx={{
                    padding: '0 !important',
                    backgroundColor: 'inherit',
                    // Overflow hidden damit Collapse sauber funktioniert
                    overflow: 'hidden',
                }}
            >
                <Collapse 
                    in={animateIn} 
                    timeout={ANIMATION_DURATION} 
                    easing="ease-out"
                >
                    <Box
                        sx={{
                            // Dezentes Padding
                            py: 2,
                            px: 3,
                            // Leichte Einrückung links
                            ml: 3,
                        }}
                    >
                        {children}
                    </Box>
                </Collapse>
            </TableCell>
        </TableRow>
    );
};

export default ExpandableRowDetail;
